import { DirectiveResult } from 'lit-html/directive';
import { unsafeHTML, UnsafeHTMLDirective } from 'lit-html/directives/unsafe-html.js';
import { property } from 'lit/decorators.js';
import { LocaleCode } from '~/common/interfaces';
import { TranslationKey, TranslationParams, TranslationService } from '~/modules/utils/i18n';
import { formatRichText } from '~/modules/utils/rich-text.helper';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Constructor<T = {}> = abstract new (...args: any[]) => T;

export interface MixinBase {
    connectedCallback?(): void;
    disconnectedCallback?(): void;
    requestUpdate(): void;
}

export function withTranslations<TBase extends Constructor<MixinBase>>(Base: TBase) {
    abstract class withTranslations extends Base {
        //TODO we need another approach for the properties
        @property({ type: String }) public lang = LocaleCode.DE_CH;
        @property({ type: String }) public currency = '';
        @property({ type: String }) public brand = '';
        @property({ type: String }) public tenant = 'kuoni';
        @property({ type: Object, attribute: 'additional-tracking-data' }) public additionalTrackingData = {};

        public translationService: TranslationService = TranslationService.getInstance();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        constructor(...args: any[]) {
            // pass arguments through to super; mixin classes require `any[]` rest parameter
            super(...args);
        }

        override connectedCallback(): void {
            super.connectedCallback?.();
            this.translationService.addEventListener(
                'der.translations-added',
                this._handleNewTranslation as EventListener
            );
        }

        override disconnectedCallback(): void {
            this.translationService.removeEventListener(
                'der.translations-added',
                this._handleNewTranslation as EventListener
            );
            super.disconnectedCallback?.();
        }

        public _handleNewTranslation = (event: CustomEvent) => {
            event.stopPropagation();
            this.requestUpdate();
        };

        // Translation methods
        public async loadTranslation(basePath: string, filePath: string): Promise<void> {
            const env: Record<string, unknown> =
                (import.meta as ImportMeta & { env?: Record<string, unknown> }).env ?? {};
            const isStorybook = Boolean(env['STORYBOOK']);
            const isNotLocal = Boolean(env['BRANCH']);
            const isI18nPath = filePath.startsWith('i18n/');

            // default: resolve relative to the module file
            const computedUrl = new URL(filePath, new URL('.', basePath)).href;

            // derive component static path used by your storybook build
            let componentUrl = '';
            try {
                const el = this as unknown as Element | undefined;
                const tag = el?.localName || el?.tagName?.toLowerCase();
                if (tag) {
                    componentUrl = `/packages/modules/src/${tag}/${filePath.replace(/^\.\/*i18n\//, '')}`;
                }
            } catch {
                componentUrl = '';
            }

            // If in Storybook and static copy exists try componentUrl first to avoid 404 noise
            const tryOrder =
                isStorybook && isNotLocal && isI18nPath && componentUrl
                    ? [componentUrl, computedUrl]
                    : [computedUrl, componentUrl].filter(Boolean);

            for (const url of tryOrder) {
                try {
                    const res = await fetch(url, { cache: 'no-cache' });
                    if (res.ok) {
                        const json = await res.json();
                        this.translationService.addTranslations(this.lang, json);
                        return;
                    }
                    // if 404 try next url
                    if (res.status === 404) continue;
                    throw new Error(`Failed to fetch translations (${res.status}) from ${url}`);
                } catch (err) {
                    // continue to next candidate, log at the end if none worked

                    console.warn(`[loadTranslation] trying ${url} failed:`, err);
                }
            }

            // final log if all attempts failed

            console.error('[loadTranslation] Could not load translation from any known path.');
        }

        trans(key: TranslationKey, params?: TranslationParams, fallback?: string): string {
            return this.translationService.translate(key, params, fallback);
        }

        transRichText(
            key: TranslationKey,
            params?: TranslationParams,
            fallback?: string
        ): DirectiveResult<typeof UnsafeHTMLDirective> {
            const translation = this.translationService.translate(key, params, fallback);
            return unsafeHTML(formatRichText(translation));
        }

        transCount(key: TranslationKey, count: number, params?: TranslationParams): string {
            return this.translationService.translatePlural(key, count, params);
        }
    }

    return withTranslations;
}
